import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService, Notification } from '../services/notificationService';
import styles from '../styles/modules/NotificationBell.module.css';

interface NotificationBellProps {
  onNotificationClick?: (notification: Notification) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onNotificationClick }) => {
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'unread' | 'all'>('unread');
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = async () => {
    try {
      const data = await notificationService.getUnreadCount();
      setUnreadCount(data.count);
      setUrgentCount(data.urgentCount);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationService.getMyNotifications({
        status: activeTab === 'unread' ? 'unread' : undefined,
        limit: 20,
      });
      setNotifications(response.notifications);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (showPanel) {
        fetchNotifications();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [showPanel, fetchNotifications]);

  useEffect(() => {
    // Close panel when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPanel]);

  const handleBellClick = () => {
    setShowPanel(!showPanel);
    if (!showPanel) {
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.isRead) {
        await notificationService.markAsRead(notification._id);
        await fetchUnreadCount();
        await fetchNotifications();
      }
      if (onNotificationClick) {
        onNotificationClick(notification);
      }
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleDismiss = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await notificationService.dismissNotification(notificationId);
      await fetchUnreadCount();
      await fetchNotifications();
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  const handleTabChange = (tab: 'unread' | 'all') => {
    setActiveTab(tab);
    setTimeout(() => fetchNotifications(), 100);
  };

  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return styles.priorityUrgent;
      case 'high':
        return styles.priorityHigh;
      case 'normal':
        return styles.priorityNormal;
      case 'low':
        return styles.priorityLow;
      default:
        return styles.priorityNormal;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return '🚨';
      case 'high':
        return '⚠️';
      case 'normal':
        return '📢';
      case 'low':
        return 'ℹ️';
      default:
        return '📢';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return t('notifications.time.justNow');
    if (seconds < 3600) return t('notifications.time.minutesAgo', { count: Math.floor(seconds / 60) });
    if (seconds < 86400) return t('notifications.time.hoursAgo', { count: Math.floor(seconds / 3600) });
    if (seconds < 604800) return t('notifications.time.daysAgo', { count: Math.floor(seconds / 86400) });
    return date.toLocaleDateString();
  };

  return (
    <div className={styles.notificationBellContainer} ref={panelRef}>
      <button className={styles.notificationBell} onClick={handleBellClick}>
        <svg className={styles.bellIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className={`${styles.notificationBadge} ${urgentCount > 0 ? styles.urgent : ''}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <div className={styles.notificationPanel}>
          <div className={styles.notificationPanelHeader}>
            <h3>{t('notifications.title')}</h3>
            <button className={styles.closeBtn} onClick={() => setShowPanel(false)}>
              ✕
            </button>
          </div>

          <div className={styles.notificationTabs}>
            <button
              className={`${styles.tab} ${activeTab === 'unread' ? styles.active : ''}`}
              onClick={() => handleTabChange('unread')}
            >
              {t('notifications.unread')} {unreadCount > 0 && `(${unreadCount})`}
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
              onClick={() => handleTabChange('all')}
            >
              {t('notifications.all')}
            </button>
          </div>

          <div className={styles.notificationList}>
            {loading ? (
              <div className={styles.notificationLoading}>{t('notifications.loading')}</div>
            ) : notifications.length === 0 ? (
              <div className={styles.notificationEmpty}>
                {activeTab === 'unread'
                  ? t('notifications.emptyUnread')
                  : t('notifications.emptyAll')}
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''} ${getPriorityClass(
                    notification.priority
                  )}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={styles.notificationIcon}>
                    {getPriorityIcon(notification.priority)}
                  </div>
                  <div className={styles.notificationContent}>
                    <div className={styles.notificationHeader}>
                      <h4>{notification.title}</h4>
                      <span className={styles.notificationTime}>
                        {getTimeAgo(notification.createdAt)}
                      </span>
                    </div>
                    <p className={styles.notificationText}>{notification.content}</p>
                    {notification.category && (
                      <span className={styles.notificationCategory}>{notification.category}</span>
                    )}
                  </div>
                  {!notification.isRead && <div className={styles.unreadDot}></div>}
                  {notification.displayType === 'dismissible' && !notification.isDismissed && (
                    <button
                      className={styles.dismissBtn}
                      onClick={(e) => handleDismiss(e, notification._id)}
                      title={t('notifications.dismiss')}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
